/**
 * @file     LessonMarkdown.tsx
 * @readme   ./README.md
 * @purpose  Renders markdown strings as styled HTML for academy lesson content. Provides consistent typography for reading bodies, slide text, and any markdown-formatted lesson field.
 */
'use client';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { hairline, headingFont, hubColors } from '@/components/hub/hubKitStyles';

interface LessonMarkdownProps {
  children: string;
  /** Base font size, defaults to the 17px reading size. */
  fontSize?: number | string;
}

const headingSx = {
  fontFamily: headingFont,
  fontWeight: fontWeightTokens.semibold,
  color: hubColors.textPrimary,
} as const;

export function LessonMarkdown({ children, fontSize = 17 }: LessonMarkdownProps) {
  const bodySx = { fontSize, lineHeight: 1.75, color: hubColors.textPrimary } as const;

  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children: c }) => (
            <Box component="h2" sx={{ ...headingSx, fontSize: { xs: 22, md: 26 }, letterSpacing: '-0.012em', lineHeight: 1.25, mt: 5, mb: 2 }}>
              {c}
            </Box>
          ),
          h2: ({ children: c }) => (
            <Box component="h2" sx={{ ...headingSx, fontSize: { xs: 22, md: 26 }, letterSpacing: '-0.012em', lineHeight: 1.25, mt: 5, mb: 2 }}>
              {c}
            </Box>
          ),
          h3: ({ children: c }) => (
            <Box component="h3" sx={{ ...headingSx, fontSize: { xs: 18, md: 20 }, lineHeight: 1.3, mt: 4, mb: 1.5 }}>
              {c}
            </Box>
          ),
          p: ({ children: c }) => (
            <Box component="p" sx={{ ...bodySx, m: 0, mb: 2.5 }}>
              {c}
            </Box>
          ),
          ul: ({ children: c }) => (
            <Box component="ul" sx={{ m: 0, pl: 3, mb: 2.5, '& li': { ...bodySx, mb: 1 } }}>
              {c}
            </Box>
          ),
          ol: ({ children: c }) => (
            <Box component="ol" sx={{ m: 0, pl: 3, mb: 2.5, '& li': { ...bodySx, mb: 1 } }}>
              {c}
            </Box>
          ),
          li: ({ children: c }) => <li>{c}</li>,
          strong: ({ children: c }) => (
            <strong style={{ fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>{c}</strong>
          ),
          em: ({ children: c }) => <em>{c}</em>,
          blockquote: ({ children: c }) => (
            <Box
              component="aside"
              sx={{
                m: 0,
                my: 3,
                px: { xs: 2.5, md: 3 },
                py: 2.5,
                borderRadius: `${layoutTokens.radius.lg}px`,
                backgroundColor: hubColors.surfaceSunken,
                '& p': { fontSize: 16, lineHeight: 1.6, mb: 1 },
                '& p:last-child': { mb: 0 },
              }}
            >
              {c}
            </Box>
          ),
          code: ({ children: c }) => (
            <Box
              component="code"
              sx={{
                px: 0.75,
                py: 0.25,
                backgroundColor: hubColors.surfaceSunken,
                fontSize: '0.9em',
                fontFamily: 'monospace',
              }}
            >
              {c}
            </Box>
          ),
          a: ({ href, children: c }) => (
            <Box
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: hubColors.link,
                textDecoration: 'underline',
                textUnderlineOffset: '0.18em',
                '@media (hover: hover)': { '&:hover': { color: hubColors.linkHover } },
                '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
              }}
            >
              {c}
            </Box>
          ),
          table: ({ children: c }) => (
            <Box sx={{ overflowX: 'auto', my: 3, border: hairline, borderRadius: `${layoutTokens.radius.lg}px` }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
                {c}
              </Box>
            </Box>
          ),
          th: ({ children: c, style }) => (
            <Box
              component="th"
              style={style}
              sx={{ textAlign: 'start', px: 2, py: 1.25, fontSize: 14, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary, borderBottom: hairline }}
            >
              {c}
            </Box>
          ),
          td: ({ children: c, style }) => (
            <Box
              component="td"
              style={style}
              sx={{ px: 2, py: 1.25, fontSize: 15, lineHeight: 1.5, color: hubColors.textPrimary, verticalAlign: 'top', 'tr:not(:last-child) > &': { borderBottom: hairline } }}
            >
              {c}
            </Box>
          ),
          hr: () => <Box component="hr" sx={{ border: 'none', borderTop: `1px solid ${hubColors.borderSubtle}`, my: 4 }} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
